import { CountryCombobox } from "@/components/ui/CountryCombobox";
import { PageHeader } from "@/components/ui/design-system";
import { Select as DesignSelect } from "@/components/ui/design-system";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  WorkflowNav,
} from "@/components/workflow/shared";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { getObligationSites, type ObligationSite } from "@/lib/companyObligations";
import {
  dohvatiGradilista,
  izmijeniGradiliste,
  spremiGradiliste,
  type Gradiliste,
} from "@/lib/gradilista";
import { procitajGreske } from "@/lib/greske";
import {
  dohvatiKorisnikePoUlozi,
  type KorisnikZaDodjelu,
} from "@/lib/korisnici";


/**
 * Sites and rosters — maintained by personnel (O-17).
 *
 * A standard form and a standard table, built straight from the design system
 * (O-14). Two things here are not cosmetic:
 *
 * - The **default shift** is what keeps the entry screen at three taps. It is
 *   configuration, changed here, and the summer start is a change to this
 *   field rather than a branch in the code (O-8).
 * - Ending an assignment sets its end date; the row is never deleted, because
 *   payroll asks who was where in a month that has already passed (O-16).
 */


export function GradilistaPage() {
  const { can, user } = useAuth();
  const canReadArchive = user?.permissions.some((item) => item.module === "company_document" && item.action === "read" && item.scope === "all") ?? false;
  const [gradilista, setGradilista] = useState<Gradiliste[] | null>(null);
  const [uredujem, setUredujem] = useState<Gradiliste | "novo" | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const canCreateObligation = can("company_obligation", "read") && can("company_obligation", "write");
  const [obligationSites, setObligationSites] = useState<{ source: Gradiliste[]; sites: ObligationSite[] } | null>(null);

  const ucitaj = useCallback(async () => {
    try {
      setGradilista(await dohvatiGradilista(false));
    } catch (error) {
      setGreska(procitajGreske(error).opcenito[0] ?? "Podaci nisu dostupni.");
      setGradilista([]);
    }
  }, []);

  useEffect(() => {
    let ziv = true;
    void (async () => {
      await Promise.resolve();
      if (ziv) await ucitaj();
    })();
    return () => {
      ziv = false;
    };
  }, [ucitaj]);

  useEffect(() => {
    if (!canCreateObligation || !gradilista) return;
    const controller = new AbortController();
    void getObligationSites(controller.signal).then((sites) => {
      if (!controller.signal.aborted) setObligationSites({ source: gradilista, sites });
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setGreska(procitajGreske(error).opcenito[0] ?? "Dostupnost novih obveza se ne može učitati.");
    });
    return () => controller.abort();
  }, [canCreateObligation, gradilista]);

  if (!gradilista) return <SkeletonTablica redaka={3} />;

  return (
    <div className="flex flex-col gap-6">
      <WorkflowNav />
      <PageHeader title="Gradilišta" actions={can("gradiliste", "create") ? <Button onClick={() => setUredujem("novo")}>Novo gradilište</Button> : undefined}/>

      {greska ? (
        <div
          role="alert"
          className="rounded-[var(--radius-card)] border border-[var(--color-status-bad-fg)]/25 bg-[var(--color-status-bad-bg)] p-4 text-[var(--color-status-bad-fg)]"
        >
          {greska}
        </div>
      ) : null}

      {uredujem ? (
        <FormaGradilista
          gradiliste={uredujem === "novo" ? null : uredujem}
          onGotovo={() => {
            setUredujem(null);
            void ucitaj();
          }}
          onOdustani={() => setUredujem(null)}
        />
      ) : null}

      {gradilista.length === 0 ? (
        <div className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
          <EmptyState
            naslov="Još nema otvorenih gradilišta"
            objasnjenje="Gradilište nosi voditelja i zadanu smjenu, a bez njega se sati ne mogu unositi."
            akcija={
              can("gradiliste", "create") ? (
                <Button onClick={() => setUredujem("novo")}>
                  Novo gradilište
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="avm-surface overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="oznaka px-4 py-3">Gradilište</th>
                <th className="oznaka px-4 py-3">Voditelj</th>
                <th className="oznaka px-4 py-3">Zadana smjena</th>
                <th className="oznaka px-4 py-3">Status</th>
                <th className="oznaka px-4 py-3">
                  <span className="sr-only">Radnje</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {gradilista.map((gradiliste) => (
                <tr
                  key={gradiliste.id}
                  className="h-[var(--spacing-row)] border-b border-[var(--color-border)] last:border-b-0"
                >
                  <td className="px-4">
                    <span className="font-medium">{gradiliste.naziv}</span>
                    {gradiliste.adresa ? (
                      <span className="text-[var(--color-muted-foreground)]">
                        {" · "}
                        {gradiliste.adresa}
                      </span>
                    ) : null}
                    {gradiliste.country_code ? (
                      <span className="block text-sm text-[var(--color-muted-foreground)]">
                        {gradiliste.country_code}
                        {gradiliste.distance_from_office_km
                          ? ` · ${gradiliste.distance_from_office_km} km od ureda`
                          : ""}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4">
                    {gradiliste.voditelj ?? (
                      <span className="text-[var(--color-muted-foreground)]">
                        nije dodijeljen
                      </span>
                    )}
                  </td>
                  <td className="broj px-4">
                    {gradiliste.smjena_pocetak}–{gradiliste.smjena_kraj}
                  </td>
                  <td className="px-4">
                    <Badge ton={gradiliste.aktivan ? "ok" : "idle"}>
                      {gradiliste.aktivan ? "Aktivno" : "Zatvoreno"}
                    </Badge>
                  </td>
                  <td className="flex min-h-[var(--spacing-row)] flex-wrap items-center gap-4 px-4 py-2">
                    {canCreateObligation && gradiliste.aktivan && obligationSites?.source === gradilista && obligationSites.sites.some((site) => site.id === gradiliste.id && site.available_type_count > 0) && <Link className="text-sm font-medium text-[var(--color-primary)]" to={`/obveze-tvrtke?tab=gradilista&new=1&site=${encodeURIComponent(gradiliste.id)}`}>Nova obveza</Link>}
                    {canReadArchive && <Link className="text-sm font-medium text-[var(--color-primary)]" to={`/obveze-tvrtke?tab=gradilista&archive=site&site=${encodeURIComponent(gradiliste.id)}`}>Arhiva dokumenata</Link>}
                    {can("ozo_zahtjev", "create") && <Link className="inline-flex min-h-11 items-center font-medium underline" to={`/ozo-zahtjevi?gradiliste=${encodeURIComponent(gradiliste.id)}`}>Treba opreme</Link>}
                    {can("gradilisni_spisak", "read") && <Link className="inline-flex min-h-11 items-center font-medium underline underline-offset-2" to={`/gradilisni-spisak/${gradiliste.id}`}>Gradilišni popis</Link>}
                    {can("gradiliste", "update") ? (
                      <button
                        type="button"
                        className="font-medium underline underline-offset-2"
                        onClick={() => setUredujem(gradiliste)}
                      >
                        Uredi
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

function FormaGradilista({
  gradiliste,
  onGotovo,
  onOdustani,
}: {
  gradiliste: Gradiliste | null;
  onGotovo: () => void;
  onOdustani: () => void;
}) {
  const [naziv, setNaziv] = useState(gradiliste?.naziv ?? "");
  const [adresa, setAdresa] = useState(gradiliste?.adresa ?? "");
  const [drzava, setDrzava] = useState(gradiliste?.country_code ?? "HR");
  const [udaljenost, setUdaljenost] = useState(
    gradiliste?.distance_from_office_km ?? "",
  );
  const [pocetak, setPocetak] = useState(gradiliste?.smjena_pocetak ?? "07:00");
  const [kraj, setKraj] = useState(gradiliste?.smjena_kraj ?? "15:00");
  const [voditeljId, setVoditeljId] = useState(
    gradiliste?.voditelj_korisnik_id ?? "",
  );
  const [zamjenikId, setZamjenikId] = useState(
    gradiliste?.zamjenik_korisnik_id ?? "",
  );
  // Printed on the site inspection list (NN 48/18 čl. 7.); all optional.
  const [brojPrijave, setBrojPrijave] = useState(
    gradiliste?.broj_prijave_gradilista ?? "",
  );
  const [voditeljTelefon, setVoditeljTelefon] = useState(
    gradiliste?.voditelj_telefon ?? "",
  );
  const [poslovodaIme, setPoslovodaIme] = useState(
    gradiliste?.poslovoda_ime ?? "",
  );
  const [poslovodaTelefon, setPoslovodaTelefon] = useState(
    gradiliste?.poslovoda_telefon ?? "",
  );
  const [znrIme, setZnrIme] = useState(gradiliste?.strucnjak_znr_ime ?? "");
  const [znrTelefon, setZnrTelefon] = useState(
    gradiliste?.strucnjak_znr_telefon ?? "",
  );
  const [voditelji, setVoditelji] = useState<KorisnikZaDodjelu[]>([]);
  const [aktivan, setAktivan] = useState(gradiliste?.aktivan ?? true);
  const [greska, setGreska] = useState<string | null>(null);
  const [sprema, setSprema] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void dohvatiKorisnikePoUlozi("voditelj", controller.signal)
      .then(setVoditelji)
      .catch(() => {
        if (!controller.signal.aborted) {
          setGreska("Popis voditelja nije dostupan.");
        }
      });
    return () => controller.abort();
  }, []);

  async function spremi(): Promise<void> {
    if (!naziv.trim()) {
      setGreska("Naziv gradilišta je obvezan.");
      return;
    }
    if (voditeljId && voditeljId === zamjenikId) {
      setGreska("Voditelj i zamjenik voditelja moraju biti različite osobe.");
      return;
    }
    setSprema(true);
    try {
      const podaci = {
        naziv: naziv.trim(),
        adresa: adresa.trim() || null,
        country_code: drzava.trim().toUpperCase() || null,
        distance_from_office_km: udaljenost || null,
        voditelj_korisnik_id: voditeljId || null,
        zamjenik_korisnik_id: zamjenikId || null,
        smjena_pocetak: pocetak,
        smjena_kraj: kraj,
        aktivan,
        broj_prijave_gradilista: brojPrijave.trim() || null,
        voditelj_telefon: voditeljTelefon.trim() || null,
        poslovoda_ime: poslovodaIme.trim() || null,
        poslovoda_telefon: poslovodaTelefon.trim() || null,
        strucnjak_znr_ime: znrIme.trim() || null,
        strucnjak_znr_telefon: znrTelefon.trim() || null,
      };
      if (gradiliste) {
        await izmijeniGradiliste(gradiliste.id, podaci);
      } else {
        await spremiGradiliste(podaci);
      }
      onGotovo();
    } catch (error) {
      const poruke = procitajGreske(error);
      setGreska(poruke.opcenito[0] ?? "Spremanje nije uspjelo.");
      setSprema(false);
    }
  }

  return (
    <section className="avm-surface flex flex-col gap-4 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
      <h2 className="text-[var(--text-card)] font-semibold">
        {gradiliste ? "Izmjena gradilišta" : "Novo gradilište"}
      </h2>

      {greska ? (
        <p role="alert" className="text-[var(--color-status-bad-fg)]">
          {greska}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="oznaka">Naziv</span>
          <input
            value={naziv}
            onChange={(dogadaj) => setNaziv(dogadaj.target.value)}
            className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Adresa</span>
          <input
            value={adresa}
            onChange={(dogadaj) => setAdresa(dogadaj.target.value)}
            className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          />
        </label>
        <CountryCombobox label="Država gradilišta" value={drzava} onChange={setDrzava} />
        <label className="flex flex-col gap-2">
          <span className="oznaka">Udaljenost od ureda (km)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={udaljenost}
            onChange={(dogadaj) => setUdaljenost(dogadaj.target.value)}
            className="broj h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Voditelj gradilišta</span>
          <DesignSelect
            aria-label="Voditelj gradilišta"
            value={voditeljId}
            onChange={(dogadaj) => setVoditeljId(dogadaj.target.value)}
            className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          >
            <option value="">— nije dodijeljen —</option>
            {voditelji.map((korisnik) => (
              <option
                key={korisnik.id}
                value={korisnik.id}
                disabled={korisnik.id === zamjenikId}
              >
                {korisnik.full_name} · {korisnik.email}
              </option>
            ))}
          </DesignSelect>
        </label>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Zamjenik voditelja</span>
          <DesignSelect
            aria-label="Zamjenik voditelja"
            value={zamjenikId}
            onChange={(dogadaj) => setZamjenikId(dogadaj.target.value)}
            className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          >
            <option value="">— nije dodijeljen —</option>
            {voditelji.map((korisnik) => (
              <option
                key={korisnik.id}
                value={korisnik.id}
                disabled={korisnik.id === voditeljId}
              >
                {korisnik.full_name} · {korisnik.email}
              </option>
            ))}
          </DesignSelect>
        </label>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Smjena — početak</span>
          <input
            type="time"
            value={pocetak}
            onChange={(dogadaj) => setPocetak(dogadaj.target.value)}
            className="broj h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Smjena — kraj</span>
          <input
            type="time"
            value={kraj}
            onChange={(dogadaj) => setKraj(dogadaj.target.value)}
            className="broj h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          />
        </label>
      </div>

      <p className="text-[var(--color-muted-foreground)]">
        Zadana smjena je ono što voditelj potvrđuje jednim dodirom. Ljetni
        raniji početak mijenja se ovdje.
      </p>

      <fieldset className="avm-surface flex flex-col gap-4 rounded-[var(--radius-card)] border border-[var(--color-border)] p-4">
        <legend className="oznaka px-2">Podaci za gradilišni popis</legend>
        <p className="text-[var(--color-muted-foreground)]">
          Ispisuje se na gradilišnom popisu koji inspektor traži na uvid (NN
          48/18, čl. 7.). Prazno polje ispisuje se kao crtica.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="oznaka">Broj prijave gradilišta</span>
            <input
              value={brojPrijave}
              onChange={(dogadaj) => setBrojPrijave(dogadaj.target.value)}
              className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Telefon voditelja gradilišta</span>
            <input
              value={voditeljTelefon}
              onChange={(dogadaj) => setVoditeljTelefon(dogadaj.target.value)}
              className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Poslovođa</span>
            <input
              value={poslovodaIme}
              onChange={(dogadaj) => setPoslovodaIme(dogadaj.target.value)}
              className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Telefon poslovođe</span>
            <input
              value={poslovodaTelefon}
              onChange={(dogadaj) => setPoslovodaTelefon(dogadaj.target.value)}
              className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Stručnjak zaštite na radu</span>
            <input
              value={znrIme}
              onChange={(dogadaj) => setZnrIme(dogadaj.target.value)}
              className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Telefon stručnjaka ZNR</span>
            <input
              value={znrTelefon}
              onChange={(dogadaj) => setZnrTelefon(dogadaj.target.value)}
              className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
            />
          </label>
        </div>
      </fieldset>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={aktivan}
          onChange={(dogadaj) => setAktivan(dogadaj.target.checked)}
          className="size-5 min-h-0"
        />
        <span>Gradilište je aktivno</span>
      </label>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => void spremi()} disabled={sprema}>
          {sprema ? "Spremanje…" : "Spremi gradilište"}
        </Button>
        <Button variant="outline" onClick={onOdustani}>
          Odustani
        </Button>
      </div>
    </section>
  );
}
